import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';

import styles from './HubCarousel.module.css';

interface HubCarouselProps {
  children: React.ReactNode;
  /**
   * On mobile, break the scroller out of `HubPage`'s `px-5` gutter so cards
   * scroll edge-to-edge (a peek of the next card sits at the screen edge), then
   * re-inset the first card by the same `px-5` so it stays aligned with the
   * section header. Desktop keeps the row inside the content container.
   */
  bleedOnMobile?: boolean;
}

// Pointer travel (px) before a press becomes a drag instead of a click.
const DRAG_THRESHOLD_PX = 5;

/**
 * Mouse drag-to-scroll, mirroring the native touch swipe on desktop. Snap is
 * suspended while dragging (via the `dragging` class) so the row tracks the
 * pointer 1:1, then re-engages on release to settle on a card. A capture-phase
 * click listener swallows the click that follows a drag so card links don't
 * navigate when the user lets go.
 *
 * Move/end listeners are bound on `window` for the duration of a press so the
 * drag keeps tracking past the element edge and release is always observed —
 * binding them on the element would miss a pointerup that lands outside it and
 * leave the drag stuck.
 */
function useDragToScroll(scrollerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) {
      return undefined;
    }

    let activePointerId: number | null = null;
    let didDrag = false;
    let startX = 0;
    let startScrollLeft = 0;

    const endGesture = () => {
      activePointerId = null;
      el.classList.remove(styles.dragging);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerEnd);
      window.removeEventListener('pointercancel', onPointerEnd);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) {
        return;
      }
      const deltaX = event.clientX - startX;
      if (!didDrag && Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
        didDrag = true;
        el.classList.add(styles.dragging);
      }
      if (didDrag) {
        event.preventDefault();
        el.scrollLeft = startScrollLeft - deltaX;
      }
    };

    const onPointerEnd = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) {
        return;
      }
      endGesture();
    };

    const onPointerDown = (event: PointerEvent) => {
      // Touch and pen already scroll natively.
      if (event.pointerType !== 'mouse' || event.button !== 0) {
        return;
      }
      activePointerId = event.pointerId;
      didDrag = false;
      startX = event.clientX;
      startScrollLeft = el.scrollLeft;
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerEnd);
      window.addEventListener('pointercancel', onPointerEnd);
    };

    const onClickCapture = (event: MouseEvent) => {
      if (didDrag) {
        event.preventDefault();
        event.stopPropagation();
        didDrag = false;
      }
    };

    const onDragStart = (event: DragEvent) => {
      // Native image/link drag ghosting fights the scroll drag.
      event.preventDefault();
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('click', onClickCapture, true);
    el.addEventListener('dragstart', onDragStart);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('dragstart', onDragStart);
      endGesture();
    };
  }, [scrollerRef]);
}

/**
 * Horizontal, snap-scrolling row of cards. Pairs with `useHubCarousel` so a
 * section header's chevron controls can scroll the same element. Swipeable on
 * touch and drag-scrollable with the mouse on desktop. A static fade on the
 * right edge hints that the row scrolls past the visible cards.
 */
export const HubCarousel = forwardRef<HTMLElement, HubCarouselProps>(
  ({ children, bleedOnMobile = false }, forwardedRef) => {
    const scrollerRef = useRef<HTMLElement | null>(null);
    const [atEnd, setAtEnd] = useState(false);

    // The right-edge fade hints at cards past the viewport; once the user has
    // scrolled to the last card there is nothing more to reveal, so it fades out.
    const updateAtEnd = useCallback(() => {
      const el = scrollerRef.current;
      if (!el) {
        return;
      }
      // 1px tolerance for sub-pixel rounding at the scroll boundary.
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    }, []);

    const setScrollerRef = useCallback(
      (node: HTMLElement | null) => {
        scrollerRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
        updateAtEnd();
      },
      [forwardedRef, updateAtEnd],
    );

    useDragToScroll(scrollerRef);

    useEffect(() => {
      const el = scrollerRef.current;
      if (!el) {
        return undefined;
      }
      updateAtEnd();
      el.addEventListener('scroll', updateAtEnd, { passive: true });
      window.addEventListener('resize', updateAtEnd);
      return () => {
        el.removeEventListener('scroll', updateAtEnd);
        window.removeEventListener('resize', updateAtEnd);
      };
    }, [updateAtEnd]);

    // The full-bleed breakout (negative margins + width:auto) can't be expressed
    // reliably through Tailwind here, so it lives in the CSS module as `.bleed`.
    const scrollerEdgeClasses = bleedOnMobile ? styles.bleed : 'w-full';

    return (
      <Box className="relative w-full">
        <Row
          ref={setScrollerRef}
          className={`${scrollerEdgeClasses} gap-4 pb-1 ${styles.scroller}`}
        >
          {children}
        </Row>
        <Box
          className={`pointer-events-none absolute top-0 right-0 bottom-0 hidden md:block transition-opacity duration-200 ${styles.edgeFade} ${
            atEnd ? 'opacity-0' : 'opacity-100'
          }`}
        />
      </Box>
    );
  },
);

HubCarousel.displayName = 'HubCarousel';
